/**
 * The server-evaluated Live Impact verdict (SE-8458).
 *
 * This is the read that replaced `CONFIGURED_RULE_CONFIG` /
 * `CONFIGURED_CLUB_BASELINE`. The backend evaluates the scenario's staged
 * actions plus the terms being typed — handed over as `draftActions`, SE-7410 —
 * against the club's OWN rules, figures and squad, so two organizations with
 * different revenue and different caps get different verdicts for identical
 * terms. Before this, every club was measured against one invented one.
 *
 * Everything decidable lives in `domain/draftEvaluation.ts`, which is pure and
 * testable without Apollo. This module is deliberately thin: it owns the query,
 * the debounce, and the last-good-answer retention, and nothing else.
 *
 * ## Why this is a separate file from `scenarioImpactSource.ts`
 *
 * Because that file may not import a generated GraphQL type — its own header
 * says so, and has since SE-7002. Folding this read into it would break that
 * prohibition, and the prohibition is stated over THERE, so anyone
 * "simplifying" the two modules into one would never meet it. It is restated
 * here for that reason. The seam is what let the fixtures be swapped for a real
 * backend read in SE-8458 without a single component learning where the verdict
 * comes from; collapsing it gives that up.
 * @module features/financial-compliance/adapters/draftEvaluationSource
 */

import { useMemo } from "react";
import { useDebounce } from "use-debounce";

import { clubNetBookValueFrom, toPanelRules } from "../domain/draftEvaluation";

import { useComplianceDraftEvaluationQuery } from "@/generated/graphql";

import type { ServerScenarioEvaluation } from "./scenarioImpactSource";
import type { ScenarioActionInput } from "@/generated/graphql";

/**
 * How long the typing must settle before the verdict is re-evaluated.
 *
 * AC4 wants the money deltas to move on every keystroke and the verdict to
 * follow on a debounce. The deltas are pure client arithmetic and never reach
 * this module, so this delay governs the verdict alone. 400ms is long enough
 * that typing "10000000" is one request rather than eight, and short enough
 * that the verdict lands while the user is still looking at the field.
 */
export const DRAFT_EVALUATION_DEBOUNCE_MS = 400;

/** What the caller must supply to get a server verdict. */
export interface DraftEvaluationInput {
  /** The scenario being edited, or null for the current squad. */
  readonly scenarioId: string | null;
  /** Staged actions plus the terms being typed, superseding their staged copy. */
  readonly draftActions: readonly ScenarioActionInput[];
  /** Skip the read entirely — nothing to evaluate, or the tier forbids it. */
  readonly skip?: boolean;
  /**
   * The roster's summed book value, or null when no player carries one.
   *
   * SE-8794: the server cannot supply the club's net book value — its
   * NET_BOOK_VALUE is per player, never among the club-scoped quantities — so
   * the caller sums its roster and this read only decides, from the tier's
   * allow-list, whether the viewer may see the total. Omitted reads as null.
   */
  readonly rosterNetBookValue?: number | null;
}

/** The server verdict, plus the usual in-flight and failure reporting. */
export interface DraftEvaluationResult {
  /** The evaluation, or null when none has arrived yet. */
  readonly evaluation: ServerScenarioEvaluation | null;
  /** True while the first evaluation is still in flight. */
  readonly loading: boolean;
  /** Non-null when the read failed. */
  readonly error: Error | null;
}

/**
 * Read the club's real compliance verdict for a draft scenario.
 * @param input - The scenario, its draft actions, and whether to skip.
 * @returns The evaluation, loading and error.
 */
export const useDraftEvaluation = (
  input: DraftEvaluationInput
): DraftEvaluationResult => {
  const {
    scenarioId,
    draftActions,
    skip = false,
    rosterNetBookValue = null,
  } = input;

  // Debounce a VALUE, not a reference. The action modals rebuild their terms
  // object on every render, so `draftActions` is a fresh array each time even
  // when nothing the user typed changed — debouncing that reference directly
  // would reset the timer on every render and the verdict would never settle.
  // The serialized form is stable whenever the deal is, which is the property
  // the debounce actually needs.
  const draftActionsKey = useMemo(
    () => JSON.stringify(draftActions),
    [draftActions]
  );
  const [settledKey] = useDebounce(
    draftActionsKey,
    DRAFT_EVALUATION_DEBOUNCE_MS
  );

  // Round-tripping is lossless here: `ScenarioActionInput` is plain JSON data,
  // and `undefined` is exactly what `toGqlAction` means by an absent key.
  const variables = useMemo(
    () => ({
      scenarioId,
      draftActions: JSON.parse(settledKey) as ScenarioActionInput[],
    }),
    [scenarioId, settledKey]
  );

  const { data, previousData, loading, error } =
    useComplianceDraftEvaluationQuery({
      variables,
      skip,
      // A CORRECTNESS constraint, not a performance tuning knob — do not
      // "optimise" this to a cached policy.
      //
      // A what-if evaluation must NEVER be written to the cache.
      // `ComplianceOverview` normalizes on organizationId + scenarioId, and
      // `layers` MERGES into that entity by `layer` + `ruleId`
      // (`providers/complianceLayersMerge.ts`, SE-8547). A cached draft read
      // would land on the SAME entity as the saved evaluation and overwrite
      // every field it selects (state, value, deviation) with the speculative
      // figures for a deal nobody has confirmed. The merge makes it worse, not
      // better: the fields the draft does NOT select would survive from the
      // real read, so the Compliance tab would show a record that mixes
      // hypothetical and actual figures, with nothing marking which is which.
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  // The previous answer stays on screen while a newer one is in flight — AC4's
  // "without flicker". `no-cache` means `data` goes undefined during a refetch,
  // so this is what keeps the panel from emptying between keystrokes.
  const overview =
    data?.complianceOverview ?? previousData?.complianceOverview ?? null;

  // Pending from the first keystroke until the request for the SETTLED terms
  // has come back — not merely while the request is open. Without the key
  // comparison the panel would look settled during the debounce window, which
  // is exactly when its figures are most out of date.
  const isPending = draftActionsKey !== settledKey || loading;

  const evaluation = useMemo<ServerScenarioEvaluation | null>(() => {
    if (overview == null) return null;
    return {
      rules: toPanelRules(overview.layers),
      clubNetBookValue: clubNetBookValueFrom(
        rosterNetBookValue,
        overview.permittedQuantities
      ),
      isPending,
    };
  }, [overview, isPending, rosterNetBookValue]);

  return {
    evaluation,
    loading,
    error: error ?? null,
  };
};
